import { axiosInstance } from "api/axios";
import { IFileInitUploadResponse } from "api/fileService";
import { createChunks } from "utils/createChunksFromBlob";
import { calculateSHA3Hash } from "utils/uploadFile";

// Upload pliku JAWNEGO (nieszyfrowanego) do repozytorium plików.
//
// Używają go moduły, których pliki są z definicji publiczne: załączniki porządku
// obrad (portal radnego) oraz akty prawa miejscowego wraz z ich XML, paczkami ZIPX
// i skanami rozstrzygnięć nadzorczych. Takie pliki świadomie nie przechodzą przez
// szyfrowanie: /file/init idzie bez KeyId, a bajty lecą do RGW w oryginale.
// Backend po przypięciu FileUri ustawia Files.Encrypted = false, żeby warstwa
// pobierania nie szukała klucza.
//
// Nie używamy utils/uploadBlob — tamten helper zawsze podnosi plik jawny, ale
// raportuje `encrypted: true`; tutaj kontrakt ma być jednoznaczny.

export interface IUploadedPublicFile {
  Uri: string;
  FileName: string;
  ContentType: string;
  Size: number;
}

const readAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const putChunk = (link: string, chunk: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", link, true);
    xhr.responseType = "blob";

    xhr.onload = function onload() {
      // S3 na PUT odpowiada 200/201/204.
      if (xhr.status === 200 || xhr.status === 201 || xhr.status === 204) {
        resolve((this.getResponseHeader("ETag") ?? "").replaceAll('"', ""));
      } else {
        reject(new Error(`Błąd wysyłania pliku do repozytorium (status ${xhr.status})`));
      }
    };

    xhr.onerror = () => reject(new Error("Błąd sieci podczas wysyłania pliku"));

    xhr.send(chunk);
  });

export const uploadPublicFile = async (
  file: File,
): Promise<IUploadedPublicFile> => {
  const buffer = await readAsArrayBuffer(file);
  const hash = await calculateSHA3Hash(buffer);

  const init = await axiosInstance.post<IFileInitUploadResponse>("/file/init", {
    ContentType: file.type,
    FileName: file.name,
    Hash: hash.replaceAll('"', ""),
    IsSigned: false,
    Size: file.size,
  });

  const { Uri, UploadId } = init.data;

  const chunks = createChunks(new Blob([buffer]));
  const etags: string[] = [];

  for (let i = 0; i < chunks.length; i += 1) {
    const link = await axiosInstance.get<string>(
      `/file/part?uri=${Uri}&partNumber=${i + 1}&uploadId=${UploadId}`,
    );

    etags.push(await putChunk(link.data, chunks[i]));
  }

  await axiosInstance.post("/file/confirm", {
    UploadId,
    Uri,
    Parts: etags.map((etag, index) => ({ ETag: etag, PartNumber: index + 1 })),
  });

  return {
    Uri,
    FileName: file.name,
    ContentType: file.type,
    Size: file.size,
  };
};
